import {
  BREAKPOINTS,
  COLORS,
  LinearProgress,
  Size,
  SystemIcons,
} from '@laerdal/life-react-components';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaMatch } from 'rooks';
import styled from 'styled-components';

const MOBILE_PROGRESS_BAR_THRESHOLD = 5;

type StepState = 'inactive' | 'active' | 'completed';

interface StepProps {
  state: StepState;
  stepNumber: number;
  label?: string;
  totalSteps: number;
}

const StepCircle = styled.div<{ state: StepState }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  
  ${({ state }) => {
    switch (state) {
      case 'inactive':
        return `
          background-color: ${COLORS.neutral_100};
          color: ${COLORS.neutral_600};
        `;
      case 'active':
        return `
          background-color: ${COLORS.primary_700};
          color: ${COLORS.neutral_20};
        `;
      case 'completed':
        return `
          background-color: ${COLORS.primary_500};
          color: ${COLORS.white};
        `;
    }
  }}
`;

const StepItem = styled.li`
  list-style: none;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Step: FC<StepProps> = ({ state, stepNumber, label, totalSteps }) => {
  const getStatusText = () => {
    switch (state) {
      case 'completed':
        return 'Completed';
      case 'active':
        return 'Current step';
      case 'inactive':
        return 'Not started';
    }
  };

  return (
    <StepItem aria-current={state === 'active' ? 'step' : undefined}>
      <StepCircle state={state} aria-hidden="true">
        {state === 'completed' ? <SystemIcons.CheckMark /> : stepNumber}
      </StepCircle>
      <VisuallyHidden>
        Step {stepNumber} of {totalSteps}: {getStatusText()}.{' '}
      </VisuallyHidden>
    </StepItem>
  );
};

const StepBar = styled.div<{ completed: boolean }>`
  height: 2px;
  flex: 1;
  margin: 0 8px;
  background-color: ${({ completed }) =>
    completed ? COLORS.primary_500 : COLORS.neutral_200};
  align-self: flex-start;
  margin-top: 16px;

  ${BREAKPOINTS.LARGE} {
    width: 2px;
    height: auto;
    min-height: 24px;
    margin: 8px 0;
    margin-left: 16px;
    align-self: stretch;
    flex:1 0 0;
  }
`;

const StepperList = styled.ol`
  display: flex;
  align-items: flex-start;
  padding: 0;
  margin: 0;
  width: 100%;

  ${BREAKPOINTS.LARGE} {
    flex-direction: column;
    justify-content: flex-start;
    flex: 1;
    flex:0;
    max-height: 352px;
  }
`;

interface StepperProps {
  steps: string[];
  currentStep: number;
  ariaLabel?: string;
}

const MobileProgressWrapper = styled.div`
  width: 100%;
`;

const Stepper: FC<StepperProps> = ({
  steps,
  currentStep,
  ariaLabel = 'Progress indicator'
}) => {
  const { t } = useTranslation('landingPage');
  const isLargeScreen = useMediaMatch(BREAKPOINTS.LARGE.replace('@media ', ''));
  const isMobile = !isLargeScreen;

  if (isMobile && steps.length > MOBILE_PROGRESS_BAR_THRESHOLD) {
    const clampedValue = Math.min(Math.max(currentStep, 0), steps.length);
    const percent =
      steps.length > 0 ? Math.round((clampedValue / steps.length) * 100) : 0;
    const label = t('{{percent}}% complete', { percent });

    return (
      <MobileProgressWrapper aria-label={ariaLabel}>
        <LinearProgress
          value={clampedValue}
          max={steps.length}
          label={label}
          size={Size.Small}
        />
      </MobileProgressWrapper>
    );
  }

  return (
    <StepperList>
      {steps.map((label, index) => {
        const stepNumber = index + 1;
        const state: StepState =
          stepNumber < currentStep
            ? 'completed'
            : stepNumber === currentStep
              ? 'active'
              : currentStep > steps.length
                ? 'completed'
                : 'inactive';

        return (
          <Fragment key={index}>
            <Step
              state={state}
              stepNumber={stepNumber}
              totalSteps={steps.length}
            />
            {index < steps.length - 1 && (
              <StepBar
                completed={stepNumber < currentStep}
                role="presentation"
                aria-hidden="true"
              />
            )}
          </Fragment>
        );
      })}
    </StepperList>
  );
};

export default Stepper;